<script setup lang="ts">
/**
 * The context rail — MAR-115.
 *
 * The right-hand column of the sheet. It answers one question the app could not answer before:
 * *which space am I in?* It shows the active client → case ladder and a summary of what that space
 * contains (documents, task progress, conversations) — the same facts as the expediente, at a
 * glance and without leaving chat.
 *
 * **It is PAPER, and it is part of the sheet** (Sistema Portafolio v0.2 §07). Everything it carries
 * — client names, NITs, counterparties, filenames, case history — belongs to the client, and by §01
 * that is paper. No change of material, no second plane, no aubergine.
 *
 * **It sits on `background`, the same paper as the working column, and the 1px `border` hairline is
 * the ONLY thing dividing them** (MAR-128). It was the recessed `muted` tier until then, hairline
 * included — two separators doing one job. The step read harder than the token math suggests because
 * the surface it is actually judged against is not `background` at all: the chat column next to it
 * renders white `card` surfaces, so the eye compares `muted` to #FFFFFF and sees a band. One
 * separator, and the recessed tier stays available for things that are genuinely recessed.
 *
 * **Retired: the aubergine rail.** Until MAR-127 this was frame — a sibling of the light region,
 * sitting on the shell ground and pairing with `IconRail` — because the `sidebar` token family in
 * the code covers "both rails". That forced an invented workaround: a "launcher rule" permitting
 * the rail to show an INDEX of client content but never its substance, purely so §01 stayed
 * technically intact. The rule was a patch over a contradiction. Two aubergine rails also put ~39%
 * of a 1440px viewport under frame, which brought back the app-inside-an-app feeling the sheet was
 * supposed to fix. Moving it onto paper resolves both at once, and drops the frame to ~18%.
 *
 * The index-only discipline survives, but for a different reason: **ergonomics, not provenance.** A
 * 300px column is a bad place to read, so this shows counts and filenames and never a note's body.
 * Now that the rail is paper, a preview here would not violate anything — it would just be a worse
 * reading surface than the one twelve pixels to its left.
 *
 * **The frame has exactly one rail now**, so this component no longer mirrors `IconRail` and no
 * longer has an app-bar cap to agree with (the bar spans the whole sheet). What remains is its own
 * width token, `--app-context-rail-width-expanded`, and `--app-rail-padding-horizontal` for the
 * collapsed strip.
 *
 * ## Three states, and only one of them persisted (MAR-128)
 *
 * `hidden` (no scope) · `collapsed` (initials chip + a glyph per section) · `expanded`. Hidden is
 * owned by `activeScope`; the other two by `contextRailExpanded`.
 *
 * **It renders nothing at all while the scope is global.** It used to render a globe and an invitation
 * to pick a space — a permanent 48px column whose entire content was an apology for having no content,
 * next to a composer that already offers "Acotar a un caso" in the place users actually scope from.
 * "Collapsed still answers the question" was the argument for never disappearing, and it survives
 * intact for the scoped case: with no space selected there is no answer to lose.
 *
 * The `<aside>` itself stays mounted at `w-0` rather than being `v-if`'d away, so appearing and
 * disappearing animate along the width transition the rail already had. A `v-if` would pop the
 * working column sideways by 48px with no transition to hang off, and scope changes are frequent.
 *
 * **Selecting a space forces it expanded**, from `setScope` → `revealContextRail()`. Restoring a
 * remembered scope deliberately does not — see `usePanelLayout.ts`.
 *
 * ## It carries no collapse control (MAR-128)
 *
 * Both rail toggles live in `AppBar` now, as a mirrored pair at the bar's two edges. This rail used
 * to hold its own, and disagreed with itself about where: top-right while expanded, bottom while
 * collapsed — so it collapsed from the top and expanded from the bottom. The section glyphs on the
 * collapsed strip still expand on click, but as shortcuts INTO a section, not as the control.
 *
 * Source of truth is `webChat.activeScope` — see `useContextRail.ts` for why the scope and not the
 * route. Feature doc: https://app.notion.com/p/3c304e9a5928813dba52e70dc8b284da
 */
import { Building2, ChevronRight, FileText, Folder, MessageSquare, PanelRight, PanelRightClose, SquareCheckBig, X } from 'lucide-vue-next'
import { useScopePanel } from '~/composables/web-chat/useScopePanel'
import DocumentRow from '~/components/documents/DocumentRow.vue'
import { useDateHelper } from '~/composables/useDateHelper'

const { t } = useI18n()
const { formatDateForDisplay } = useDateHelper()
const webChat = useWebChatStore()
const { contextRailExpanded, contextRailSuppressed, toggleContextRail } = usePanelLayout()
/**
 * Both rail toggles normally live in `AppBar` (MAR-128) — this rail deliberately carries none of
 * its own, having once disagreed with itself about where (top-right expanded, bottom collapsed).
 * `AppBar` now hides itself entirely while the chat document preview is open (MAR-437 follow-up),
 * which took its toggle down with it — so THIS is the fallback, shown only then, at one consistent
 * spot (the rail's own top) in both states, rather than reintroducing the old inconsistency.
 */
const showOwnToggle = computed(() => !!webChat.previewDocument)
const { data, load, refreshConversations } = useContextRail()
const { open: scopePanelOpen } = useScopePanel()

const scope = computed(() => webChat.activeScope)
const isCase = computed(() => scope.value.kind === 'case')
const isGlobal = computed(() => scope.value.kind === 'global')

/**
 * Width, border and padding vary together across the three states, so they are decided once here
 * rather than as three utilities racing each other in the class list — `border-l-0` beside `border-l`
 * is settled by stylesheet source order, not by which one appears later in the attribute, so the
 * hidden state cannot reliably switch its own hairline off that way.
 */
const railClass = computed(() => {
  if (isGlobal.value) return 'w-0 px-0'
  const chrome = 'border-l border-border pt-1 pb-1.5 px-(--app-rail-padding-horizontal)'
  // A wide-content claim narrows the rail without touching the preference — see
  // `claimWideContent` in `usePanelLayout`. It never HIDES it: the rail answers "what can Marcus
  // see", and a batch run fanning one document out per person is when that matters most.
  return contextRailExpanded.value && !contextRailSuppressed.value
    ? `w-(--app-context-rail-width-expanded) ${chrome}`
    : `w-(--app-rail-width) items-center ${chrome}`
})

/** Only trust the payload once it belongs to the scope we are actually in (a load in flight for a
 *  previous scope must not paint the previous case's documents under this case's name). */
const ready = computed(() => data.value.scope.kind === scope.value.kind
  && (data.value.scope as { id?: string }).id === (scope.value as { id?: string }).id)

const clientName = computed(() => {
  if (scope.value.kind === 'case') return scope.value.clientName || data.value.client?.name || ''
  return t('contextRail.client')
})
const spaceName = computed(() => (scope.value.kind === 'global' ? '' : scope.value.name))

/**
 * Two letters, so the collapsed rail still names the space.
 *
 * Taken from the CLIENT first, not the case. Case names are descriptive and share their opening
 * words — "Contrato laboral — Gutiérrez / TECNOVA" and every other "Contrato laboral …" all
 * collapsed to "CL", which is a badge that identifies nothing. A client name is the distinguishing
 * part of the pair, and on the client rung it is the only part there is.
 *
 * Short words that carry no identity ("de", "y", "S.A.") are skipped, so "Comercial Salvadoreña
 * S.A. de C.V." reads CS rather than CS's first two tokens by accident.
 */
const STOPWORDS = new Set(['de', 'del', 'la', 'las', 'el', 'los', 'y', 'e', 'sa', 'sl', 'cv', 'srl', 'gmbh', 'inc', 'ltd'])
const initials = computed(() => {
  const words = (clientName.value || spaceName.value)
    .replace(/[^\p{L}\p{N} ]/gu, ' ')
    .trim()
    .split(/\s+/)
    .filter(Boolean)
  const meaningful = words.filter(w => !STOPWORDS.has(w.toLowerCase()))
  return (meaningful.length ? meaningful : words)
    .slice(0, 2)
    .map(w => w[0]?.toUpperCase() ?? '')
    .join('') || '—'
})

const caseItem = computed(() => (ready.value ? data.value.caseItem : null))
const documents = computed(() => (ready.value ? data.value.documents : []))
const conversations = computed(() => (ready.value ? data.value.conversations : []))
const cases = computed(() => (ready.value ? data.value.cases : []))

const taskTotal = computed(() => caseItem.value?.task_total ?? 0)
const taskDone = computed(() => caseItem.value?.task_done ?? 0)
const taskPct = computed(() => (taskTotal.value ? Math.round((taskDone.value / taskTotal.value) * 100) : 0))

const statusLine = computed(() => {
  if (scope.value.kind === 'client') {
    // Not `${n} ${cases}` — that label is always plural, so a client with one case read "1 casos".
    const n = data.value.client?.case_count ?? cases.value.length
    return t('contextRail.caseCount', { n }, n)
  }
  const parts: string[] = []
  if (caseItem.value?.status) parts.push(t(`clients.status.${caseItem.value.status}`))
  if (caseItem.value?.due_date) parts.push(formatDateForDisplay(caseItem.value.due_date))
  return parts.join(' · ')
})

const openHref = computed(() => {
  if (scope.value.kind === 'case') return `/app/cases/${scope.value.id}`
  if (scope.value.kind === 'client') return `/app/clients/${scope.value.id}`
  // Unscoped: the cases list, which is where the section starts (MAR-130).
  return '/app/cases'
})

/**
 * The one watcher in this surface, and the case watchers exist for: the rail's entire content is a
 * projection of `activeScope`, which is written from five unrelated places (the scope bar, the scope
 * panel, the case hub, a conversation switch, and the remembered-scope restore). Hooking each call
 * site would leave the "which space am I in" indicator silently stale the day a sixth is added —
 * exactly the failure this surface exists to prevent.
 */
watch(scope, s => void load(s), { immediate: true, deep: false })
// The rail's ONE exception to "a projection of the scope": a conversation started inside the rail's
// case changes the case's conversation list without changing the scope (MAR-1468).
watch(() => webChat.conversationId, id => void refreshConversations(id))

function openScopePicker() {
  scopePanelOpen.value = true
}

/**
 * Unset the scope from the surface that displays it (MAR-128). Clearing existed only in the
 * composer's scope bar — the rail showed the state it could not release.
 *
 * The rail disappears as a result, which is a large change to make from a small button, so it is
 * deliberately reversible in one step and in an obvious place: the composer's "Acotar a un caso" is
 * still on screen the moment this rail leaves, and the composer stays the primary way to scope.
 */
function clearScope() {
  void webChat.setScope({ kind: 'global' })
}
</script>

<template>
  <!-- Same paper as the working column, divided from it by a 1px `rule` hairline and nothing else
       (MAR-128). `w-0` rather than `v-if` for the unscoped state: the rail comes and goes as often as
       the scope changes, and the width transition is what keeps that from throwing the working column
       48px sideways with no animation to hang off. `overflow-hidden` so nothing leaks at `w-0`. -->
  <aside
    class="shrink-0 hidden sm:flex flex-col overflow-hidden bg-background text-foreground transition-all duration-200"
    :class="railClass"
  >
    <template v-if="!isGlobal">
    <!-- ── Collapsed: the space, still named ── -->
    <template v-if="!contextRailExpanded || contextRailSuppressed">
      <button
        v-if="showOwnToggle"
        type="button"
        class="mb-1.5 flex h-6 w-full items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-card hover:text-foreground"
        :title="t('contextRail.expand')"
        :aria-label="t('contextRail.expand')"
        @click="toggleContextRail"
      >
        <PanelRight class="h-3.5 w-3.5" />
      </button>
      <button
        type="button"
        class="h-8 w-full rounded-lg border border-border bg-card text-[11px] font-semibold text-accent-ink transition-colors hover:bg-accent-surface"
        :title="spaceName || clientName"
        @click="toggleContextRail"
      >
        {{ initials }}
      </button>

      <div class="mt-2.5 flex w-full flex-col items-center gap-1">
        <span class="h-px w-5 bg-border" />
        <button
          v-if="isCase"
          type="button"
          class="relative flex h-8 w-full items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-card"
          :title="`${documents.length} ${t('contextRail.documents')}`"
          @click="toggleContextRail"
        >
          <FileText class="h-4 w-4" />
          <span v-if="documents.length" class="absolute right-0.5 top-0.5 text-[8.5px] font-semibold text-muted-foreground">{{ documents.length }}</span>
        </button>
        <button
          v-if="isCase && taskTotal"
          type="button"
          class="flex h-8 w-full items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-card"
          :title="`${t('contextRail.tasks')} ${taskDone}/${taskTotal}`"
          @click="toggleContextRail"
        >
          <SquareCheckBig class="h-4 w-4" />
        </button>
        <button
          v-if="!isCase"
          type="button"
          class="relative flex h-8 w-full items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-card"
          :title="`${cases.length} ${t('contextRail.cases')}`"
          @click="toggleContextRail"
        >
          <Folder class="h-4 w-4" />
          <span v-if="cases.length" class="absolute right-0.5 top-0.5 text-[8.5px] font-semibold text-muted-foreground">{{ cases.length }}</span>
        </button>
        <button
          v-if="isCase && conversations.length"
          type="button"
          class="relative flex h-8 w-full items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-card"
          :title="`${conversations.length} ${t('contextRail.conversations')}`"
          @click="toggleContextRail"
        >
          <MessageSquare class="h-4 w-4" />
          <span class="absolute right-0.5 top-0.5 text-[8.5px] font-semibold text-muted-foreground">{{ conversations.length }}</span>
        </button>
      </div>
    </template>

    <!-- ── Expanded ── -->
    <template v-else>
      <!-- The ladder IS the header (MAR-128) — there is no caption above it. `ESPACIO ACTIVO` named
           the panel to someone already looking at the panel, four pixels above the line that names
           the actual space. The rail is also never on screen unscoped any more, so there is no
           "what is this column" moment left for a caption to answer.

           The ladder is the switch: the client rung small and quiet above the rung you are on, the
           whole block opening the same scope panel the composer uses. `X` unsets, which hides the
           rail — the one control here that removes its own surface, so it stays a deliberate icon
           button rather than anything that could be hit on the way to the case name. -->
      <div class="px-2.5 pt-1 pb-3.5">
        <div v-if="showOwnToggle" class="flex justify-end">
          <button
            type="button"
            class="rounded-md p-1 text-muted-foreground transition-colors hover:bg-card hover:text-foreground"
            :title="t('contextRail.collapse')"
            :aria-label="t('contextRail.collapse')"
            @click="toggleContextRail"
          >
            <PanelRightClose class="h-3.5 w-3.5" />
          </button>
        </div>
        <div class="flex items-start gap-1">
          <button
            type="button"
            class="group min-w-0 flex-1 text-left"
            :title="t('contextRail.switch')"
            @click="openScopePicker"
          >
            <p class="truncate text-[11px] uppercase tracking-wide text-muted-foreground">{{ clientName }}</p>
            <p class="mt-0.5 text-[15px] leading-snug text-foreground transition-colors group-hover:text-accent-ink">{{ spaceName }}</p>
          </button>
          <button
            type="button"
            class="shrink-0 rounded-md p-1 text-muted-foreground transition-colors hover:bg-card hover:text-foreground"
            :title="t('contextRail.unset')"
            :aria-label="t('contextRail.unset')"
            @click="clearScope"
          >
            <X class="h-3.5 w-3.5" />
          </button>
        </div>
        <p v-if="statusLine" class="mt-2 flex items-center gap-1.5 text-[11px] text-muted-foreground">
          <span class="h-1.5 w-1.5 shrink-0 rounded-full bg-tertiary" />
          {{ statusLine }}
        </p>
      </div>

      <div class="min-h-0 flex-1 overflow-y-auto rail-scroll">
        <!-- The chat's current workflow negotiation (MAR-437 follow-up) — deliberately its own
             section, ahead of everything else. It is conversation-scoped and changes every turn
             while a proposal is being negotiated, unlike the case/client facts below it, which are
             durable and slow-changing; keeping it separate is what keeps those two lifecycles from
             reading as one blended thing. Renders nothing without an open or just-finished draft. -->
        <WorkflowContextRailWorkflowDraft />

        <!-- Case rung: documents, task progress, conversations. -->
        <template v-if="isCase">
          <section class="border-t border-border px-2.5 py-3.5">
            <div class="mb-2 flex items-baseline justify-between text-xs text-muted-foreground">
              <span>{{ t('contextRail.documents') }}</span>
              <span class="tabular-nums">{{ documents.length }}</span>
            </div>
            <!-- The same row the case hub and the document browser use. The rail used to draw a
                 generic page glyph with no file type and no status, so the identical document read as
                 a different object depending on which panel you looked at it in. -->
            <DocumentRow
              v-for="doc in documents.slice(0, 3)"
              :key="doc.link_id"
              :document="doc"
              :to="`/app/docs/${doc.id}?caseId=${(scope as { id: string }).id}`"
              dense
            />
            <p v-if="!documents.length" class="text-[13px] text-muted-foreground">{{ t('contextRail.noDocuments') }}</p>
            <NuxtLink
              v-if="documents.length > 3"
              :to="`/app/cases/${(scope as { id: string }).id}/documents`"
              class="mt-1.5 inline-block text-xs text-muted-foreground hover:text-foreground"
            >
              {{ t('contextRail.seeAll', { count: documents.length }) }}
            </NuxtLink>
          </section>

          <section v-if="taskTotal" class="border-t border-border px-2.5 py-3.5">
            <div class="mb-2 flex items-baseline justify-between text-xs text-muted-foreground">
              <span>{{ t('contextRail.tasks') }}</span>
              <span class="tabular-nums">{{ taskDone }} / {{ taskTotal }}</span>
            </div>
            <div class="h-1 overflow-hidden rounded-full bg-border">
              <div class="h-full rounded-full bg-muted-foreground transition-[width] duration-300" :style="{ width: `${taskPct}%` }" />
            </div>
          </section>

          <!-- Rendered unconditionally, like Documentos above it. It used to be `v-if` on the count,
               which made `contextRail.noConversations` unreachable and made the rail's sections
               inconsistent: two of them said "nothing yet" and the third silently vanished. -->
          <section class="border-t border-border px-2.5 py-3.5">
            <div class="mb-2 flex items-baseline justify-between text-xs text-muted-foreground">
              <span>{{ t('contextRail.conversations') }}</span>
              <span class="tabular-nums">{{ conversations.length }}</span>
            </div>
            <!-- `@click` is not decoration: the URL alone does not load a conversation. The docked
                 panel is already mounted, so navigating to /app/c/<id> changed the address bar while
                 the pane sat on the start screen. The sidebar's own recents list has always called
                 `loadConversation` alongside its link; this had only half of it. -->
            <NuxtLink
              v-for="conv in conversations.slice(0, 3)"
              :key="conv.id"
              :to="`/app/c/${conv.id}`"
              class="-mx-2 flex items-center gap-2.5 rounded-lg px-2 py-1.5 text-[13px] text-foreground transition-colors hover:bg-card"
              @click="webChat.loadConversation(conv.id)"
            >
              <MessageSquare class="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
              <span class="truncate">{{ conv.title || t('clients.hub.untitledChat') }}</span>
            </NuxtLink>
            <p v-if="!conversations.length" class="text-[13px] text-muted-foreground">{{ t('contextRail.noConversations') }}</p>
          </section>
        </template>

        <!-- Client rung: its cases. -->
        <section v-else class="border-t border-border px-2.5 py-3.5">
          <div class="mb-2 flex items-baseline justify-between text-xs text-muted-foreground">
            <span>{{ t('contextRail.cases') }}</span>
            <span class="tabular-nums">{{ cases.length }}</span>
          </div>
          <NuxtLink
            v-for="c in cases.slice(0, 6)"
            :key="c.id"
            :to="`/app/cases/${c.id}`"
            class="-mx-2 flex items-center gap-2.5 rounded-lg px-2 py-1.5 text-[13px] text-foreground transition-colors hover:bg-card"
          >
            <Folder class="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
            <span class="truncate">{{ c.name }}</span>
          </NuxtLink>
          <p v-if="!cases.length" class="text-[13px] text-muted-foreground">{{ t('contextRail.noCases') }}</p>
        </section>
      </div>

      <div class="mt-auto border-t border-border px-2.5 pt-2.5">
        <NuxtLink
          :to="openHref"
          class="flex items-center justify-center gap-1.5 rounded-lg border border-border py-2 text-[13px] text-foreground transition-colors hover:bg-card"
        >
          <Building2 v-if="!isCase" class="h-3.5 w-3.5 text-muted-foreground" />
          {{ isCase ? t('contextRail.openCase') : t('contextRail.openClient') }}
          <ChevronRight class="h-3.5 w-3.5 text-muted-foreground" />
        </NuxtLink>
      </div>
    </template>
    </template>
  </aside>
</template>

<style scoped>
/* Same treatment as IconRail's `.rail-scroll` — a default scrollbar is tuned for light ground and
   reads as a stripe against the frame ground. */
.rail-scroll {
  scrollbar-width: thin;
  scrollbar-color: var(--color-input) transparent;
}
.rail-scroll::-webkit-scrollbar { width: 6px; }
.rail-scroll::-webkit-scrollbar-track { background: transparent; }
.rail-scroll::-webkit-scrollbar-thumb {
  background-color: var(--color-input);
  border-radius: 3px;
}
</style>
